import { isProduction } from "./environment";
import { VirtualFolderInfo } from "./folderIdUtils/types";

export const SERVICE_NAME = "assetmanagement";
const domain = `zapier${isProduction ? "" : "-staging"}.com`;

// ********************************************************
// API BASE URLS
// ********************************************************

export const MONOLITH_BASE_URL =
  process.env.NEXT_PUBLIC_MONOLITH_BASE_URL ||
  process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
// ConnectApp serves the new auth-connection flow on its own domain. The
// monolith redirects /engine/auth/start/... popups to this origin, so the
// App Connections page must accept postMessages from it after a connection
// completes. Mirrors the backend CONNECT_APP_BASE_URL setting.
export const CONNECT_APP_BASE_URL =
  process.env.NEXT_PUBLIC_CONNECT_APP_BASE_URL || `https://connect.${domain}`;
export const AGENTS_API_BASE =
  process.env.NEXT_PUBLIC_AGENTS_API_BASE || `https://agents.${domain}`;
export const CHATBOTS_API_BASE =
  process.env.NEXT_PUBLIC_CHATBOTS_API_BASE || `https://chatbotsapi.${domain}`;
export const CODE_SUBSTRATE_WORKFLOWS_API_BASE =
  process.env.NEXT_PUBLIC_CODE_SUBSTRATE_WORKFLOWS_API_BASE ||
  `https://code-substrate-workflows.${domain}`;
// formsapi is served on the main host behind ATC (`/api/forms/*`), not on its
// own subdomain.
export const FORMSAPI_API_BASE =
  process.env.NEXT_PUBLIC_FORMSAPI_API_BASE || `https://${domain}/api/forms`;
export const INTERFACES_API_BASE =
  process.env.NEXT_PUBLIC_INTERFACES_API_BASE || `https://interfaces.${domain}`;
export const CANVAS_API_BASE =
  process.env.NEXT_PUBLIC_CANVAS_API_BASE || MONOLITH_BASE_URL;
export const TABLES_API_BASE =
  process.env.NEXT_PUBLIC_TABLES_API_BASE || `https://tables.${domain}`;
export const PERMISSIONS_API_BASE =
  process.env.NEXT_PUBLIC_PERMISSIONS_API_BASE ||
  `https://permissions-api.${domain}`;
// approvalsapi's public front door, same pattern as agents/permissions-api.
// Not the internal approvalsapi.k8s hostname (private address, unreachable
// from Vercel) and not the cell hostname (atc-route, rejects non-ATC traffic).
// The production public-route for this hostname ships via argo-cd-configs;
// do not promote this to production before that lands.
export const APPROVALSAPI_API_BASE =
  process.env.NEXT_PUBLIC_APPROVALSAPI_BASE || `https://approvalsapi.${domain}`;
export const ORGANIZATION_API_BASE =
  process.env.NEXT_PUBLIC_ORGANIZATION_API_BASE || MONOLITH_BASE_URL;
export const ZSL_API_BASE =
  process.env.NEXT_PUBLIC_ZSL_API_BASE || `https://zsl-api.${domain}`;
export const USER_PROPERTY_API_BASE =
  process.env.NEXT_PUBLIC_USER_PROPERTY_API_BASE ||
  `https://usercontextapi.${domain}`;
export const TEMPLATES_API_BASE =
  process.env.NEXT_PUBLIC_TEMPLATES_API_BASE || `https://templates.${domain}`;
export const SIDEKICK_API_BASE =
  process.env.NEXT_PUBLIC_SIDEKICK_API_BASE || `https://sidekick.${domain}`;
export const PORTER_BASE_URL =
  process.env.NEXT_PUBLIC_PORTER_ORIGIN ||
  `https://porter.vercel.${domain}/api/graphql`;

// API Routes
export const BFF_API_ROUTE = "/api/asset-management-bff/trpc";
/** The same router on a segment with a longer function budget, for streaming procedures. */
export const BFF_STREAM_API_ROUTE = "/api/asset-management-bff/trpc-stream";

// Presence of this header proves a request came from same-origin app JS: browsers
// block cross-origin custom headers without a preflight the BFF never grants.
export const CSRF_HEADER_NAME = "x-zapier-csrf";
export const PROJECTS_API_ROUTE = `/api/v1/projects`;

// ********************************************************
// Product Routes
// ********************************************************

// Agents
export const AGENTS_UI_BASE_URL = `https://agents.${domain}`;
export const AGENT_ROUTE = `${AGENTS_UI_BASE_URL}/bots/`;
export const AGENT_CREATE_ROUTE = `${AGENTS_UI_BASE_URL}?action=create-agent`;

// Interfaces
export const INTERFACES_UI_BASE_URL = `https://interfaces.${domain}`;
export const INTERFACE_ROUTE = `${INTERFACES_UI_BASE_URL}/interfaces/project`;
export const INTERFACE_HELP_ROUTE = `https://zapier-interfaces.zapier.app/home`;
export const INTERFACE_CREATE_ROUTE = `${INTERFACES_UI_BASE_URL}/interfaces/get-started`;
export const INTERFACE_EMPTY_ROUTE = `${INTERFACES_UI_BASE_URL}/interfaces/new`;

// Tables
export const TABLES_UI_BASE_URL = `https://tables.${domain}`;
export const TABLE_ROUTE = `${TABLES_UI_BASE_URL}/app/tables`;
export const TABLE_CREATE_ROUTE = `${TABLES_UI_BASE_URL}/app/tables/get-started`;
export const TABLE_EMPTY_ROUTE = `${TABLES_UI_BASE_URL}/app/tables/home`;
export const TABLE_IMPORT_ROUTE = `${TABLES_UI_BASE_URL}/app/tables/import`;
export const TABLES_USER_GUIDE_ROUTE =
  "https://tables-feedback.zapier.app/how-to-videos";

// Chatbots
export const CHATBOT_ROUTE = "/app/chatbots/";
export const CHATBOT_CREATE_ROUTE = "/app/chatbots/create";

// Canvas
export const CANVAS_ROUTE = "/app/canvas/";
export const CANVAS_EMPTY_ROUTE = "/app/canvas/getting-started";
export const CANVAS_CREATE_ROUTE = "/app/canvas/new";
export const CANVAS_VIEW_BASE_PATH = `https://${domain}/app/canvas/public`;
export const CANVAS_ALL_GUIDES_URL =
  "https://help.zapier.com/hc/en-us/categories/19840274478093-Canvas";

// Next Gen Zaps
export const NEXT_GEN_ZAP_CREATE_ROUTE = "/workflow/create";

// Templates
export const TEMPLATES_HELP_URL =
  "https://help.zapier.com/hc/en-us/articles/43465487495181-Guided-templates";

// Other
export const EDITOR_ROUTE = "/webintent/edit-zap/";
export const CONTACT_SALES_PATH = "/l/contact-sales";

// ********************************************************
// EVENTS
// ********************************************************

export const EVENTS_SYSTEM_NAME = "assetmanagement";
export const USER_INTERACTION_EVENT =
  "web_analytics.tracking.user_interaction.UserInteractionEvent";
export const HIDE_MOVE_ZAP_WARNING = "hideMoveZapToSharedFolderWarning";
export const DEFAULT_VIEW_SETTINGS = "defaultViewSettings";

// ********************************************************
// PLACEHOLDERS
// ********************************************************

export const PLACEHOLDER_ICON = `https://cdn.${domain}/zapier/images/frontend/placeholder/64x64-new.grey.png`;
export const PLACEHOLDER_PROFILE_ICON = `https://cdn.${domain}/zapier/images/account-default-image.svg`;
export const PLACEHOLDER_APP_ICON =
  "https://cdn.zapier.com/zapier/images/frontend/placeholder/64x64-new.grey.png";
export const EMPTY_SET_PROJECTS =
  "https://cdn.zapier.com/storage/photos/c5d941fb5004bc0f085c4e7fd7303dfc.png";
export const TEAMS_UPSELL =
  "https://cdn.zapier.com/storage/photos/c583d3a0639dbc3eda45a04c291f7e23.png";

// ********************************************************
// PAGE DEFAULTS
// ********************************************************

export const ZAP_PAGE_SIZE = 25;
export const FOLDERS_PAGE_SIZE = 25;
export const PROJECTS_PAGE_SIZE = 25;
export const CONNECTIONS_PAGE_SIZE = 25;
export const CONNECTION_STATUS_NON_COMPLIANT = "non-compliant";
export const APPS_PAGE_SIZE = 25;
export const NEXT_GEN_ZAPS_PAGE_SIZE = 25;
export const NEXT_GEN_FORMS_PAGE_SIZE = 25;
// Where users add the Next Gen Forms integration to an MCP server.
export const ZAPIER_MCP_URL = "https://mcp.zapier.com";
export const FOLDER_ASSETS_PAGE_SIZE = 25;
export const FOLDER_ASSETS_SORT_LIMIT = 1000;
export const BULK_ASSET_ACTION_LIMIT = 100;
export const APPS_SORT_LIMIT = 1000;
export const UNTITLED_ZAP_DEFAULT = "Untitled Zap";
export const UNTITLED_ZAP_TITLE = `${UNTITLED_ZAP_DEFAULT}   `; // 💡 important: this string has 3 trailing spaces for find/replace
export const UNTITLED_CONNECTION_DEFAULT = "Untitled connection";
export const UNTITLED_CONNECTION_TITLE = `${UNTITLED_ZAP_DEFAULT}   `; // 💡 important: this string has 3 trailing spaces for find/replace

// ********************************************************
// ASSET ID PREFIXES
// ********************************************************

export const ZAP_ASSET_ID_PREFIX = "zap:";
export const CANVAS_ASSET_ID_PREFIX = "canvas:";
export const TABLE_ASSET_ID_PREFIX = "table:";
export const TABLE_VIEW_ASSET_ID_PREFIX = "tableView:";

// ********************************************************
// ERROR CODES
// ********************************************************

/** The BFF's code for a move refused because the destination is a private folder owned by someone other than the Zap owner. */
export const PRIVATE_FOLDER_OWNER_MISMATCH_ERROR_CODE =
  "private-folder-owner-mismatch";

// ********************************************************
// FOLDER DEFAULTS
// ********************************************************

export const DEFAULT_FOLDER_ID = "0";
export const FOLDER_TRASH_ID = "-1";
export const FOLDER_PENDING_ID = "-3";
// Queued actions is not a folder but displays as a folder item on sidebar
export const FOLDER_QUEUED_ACTIONS_ID = "-4";
// Pending approvals is not a folder but displays as a folder item on sidebar
export const FOLDER_PENDING_APPROVAL_ID = "-5";
// Temporary folder id is used for new folders that are created but not yet saved to the database, this is used for cache lookup
export const TEMP_FOLDER_ID = "-6";
export const DEFAULT_FOLDER_NAME = "Personal";
export const PENDING_FOLDER_NAME = "Pending billing";
export const TRASH_FOLDER_NAME = "trash";

export const virtualFolderNames: Record<string, string> = {
  my_zaps_default_folder: DEFAULT_FOLDER_ID,
  personal: DEFAULT_FOLDER_ID,
  root: DEFAULT_FOLDER_ID,
  home: DEFAULT_FOLDER_ID,
  pending: FOLDER_PENDING_ID,
  trash: FOLDER_TRASH_ID,
};

export const virtualFolderIds = Object.values(virtualFolderNames);

export const virtualFoldersById: Record<string, VirtualFolderInfo> = {
  [DEFAULT_FOLDER_ID]: {
    href: "/app/assets/zaps",
    name: DEFAULT_FOLDER_NAME,
  },
  [FOLDER_PENDING_ID]: {
    href: "/app/assets/zaps?virtual=Pending+billing",
    name: PENDING_FOLDER_NAME,
  },
  [FOLDER_TRASH_ID]: {
    href: "/app/assets/zaps/folders/trash",
    name: TRASH_FOLDER_NAME,
  },
};

export const GOVERNANCE_INBOX_API_BASE =
  process.env.GOVERNANCE_INBOX_BASE_URL ||
  `https://governanceinbox.${domain}/api/inbox`;
